import React, { Dispatch, SetStateAction, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Text } from 'components';
import LoginForm from './LoginForm';
import axios from 'axios';
import { getDecodeCookie, setCookie } from 'common/Cookie';
import { useDispatch } from 'react-redux';
import { login } from 'slices/loginSlice';
import _ from 'lodash';
import ThirdPartyLoginButtonSet from './ThirdPartyLoginButtonSet';
import { CredentialResponse, GoogleLogin } from '@react-oauth/google';

interface Props {
  isEmailFormShown: boolean;
  setIsEmailFormShown: Dispatch<SetStateAction<boolean>>;
}

const SigninForm = ({ isEmailFormShown, setIsEmailFormShown }: Props) => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const nav = useNavigate();
  const dispatch = useDispatch();
  const [googleAuthMode, setGoogleAuthMode] = useState<'login' | 'convert'>('login');
  const [pendingCode, setPendingCode] = useState<string | null>(null);

  async function onClickEmailButton() {
    if (isEmailFormShown) {
      try {
        const response = await axios.post('/api/member/login', {
          email,
          password,
        });
  
        // 로그인 성공 처리
        if (response.data.code === 'SUCCESS') {
          const { accessToken } = response.data.data;
          setCookie('accessToken', accessToken);
          const token = getDecodeCookie('accessToken');
          if (token != null) {
            const payload = _.cloneDeep(token);
            nav('/get-started');
            dispatch(login(payload));
          }
        }
      } catch (error) {
        // 에러 로그 확인을 위한 추가 정보 출력
        console.log('Login error:', error);
  
        // Axios 에러 확인
        if (axios.isAxiosError(error)) {
          if (error.response) {
            console.log('Error response:', error.response);
            if (error.response.data.method === 'LOGIN_ERROR') {
              if (error.response.data.failedCount !== undefined) {
                const logCount = error.response.data.failedCount;
                const Count = 5-logCount;
                console.log(`The password is incorrect. Your account will be locked for 24 hours after ${Count} more failed attempts.`);
                setErrorMessage(`The password is incorrect. Your account will be locked for 24 hours after ${Count} more failed attempts.`);
              } else {
                setErrorMessage('Invalid login credentials. Please try again.');
                nav('/login');
              }
            }else if(error.response.data.method==='ACCOUNT_LOCKED'){
              setErrorMessage(`Your account has been locked due to multiple failed login attempts. You will be unable to log in for the next 24 hours. For assistance, please contact us by email.`);
            }else if(error.response.data.method==='SOCIAL_LOGIN_TRY'){
              setErrorMessage('Please try again using your social login.');
            }else{
              nav('/*?code=UNKNOWN_ERROR');
            }
          }
        } 
      }
    } else {
      setIsEmailFormShown(true);
    }
  }


  const signinButtonCallbackObj = {
    onClickGoogleButton: async (code: string, redirectUri: string) => {
      try {
        const endpoint = googleAuthMode === 'convert' ? '/api/member/auth/google/convert' : '/api/member/auth/google';
        const response = await axios.post(endpoint, { code, redirectUri });

        if (response.data.code === 'SUCCESS') {
          const { accessToken } = response.data.data;
          setCookie('accessToken', accessToken);
          const token = getDecodeCookie('accessToken');
          if (token != null) {
            const payload = _.cloneDeep(token);
            nav('/get-started');
            dispatch(login(payload));
          }
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const errorData = error.response?.data;
          console.log('error', error);
          console.log('errorData', errorData);

          if (errorData?.method === 'EXIST_LOCAL_EMAIL') {
            const confirmConvert = window.confirm('이미 가입된 이메일입니다. 구글 계정으로 전환하시겠습니까?');

            if (confirmConvert) {
              setGoogleAuthMode('convert');
              alert('구글 계정 전환을 위해 "구글 로그인 버튼"을 다시 클릭해주세요.');
            } else {
              alert('이메일 로그인 후 소셜 전환을 진행하세요.');
              nav('/login');
            }
          } else if (errorData?.method === 'EMAIL_NOT_VERIFIED') {
            alert('이메일 인증이 완료되지 않았습니다. 이메일 인증을 먼저 완료하세요.');
            nav('/login');
          } else if (errorData?.method === 'GOOGLE_LOGIN_FAILED') {
            alert('구글 로그인에 실패했습니다. 다시 시도해주세요.');
            nav('/login');
          } else {
            alert('알 수 없는 오류가 발생했습니다.');
            nav('/*?code=UNKNOWN_ERROR');
          }
        } else {
          console.error('Unhandled Google login error:', error);
        }
      }
    },
  };
  function onClickSignUpButton() {
    nav('/sign-up');
  }

  return (
    <div className="flex w-80 flex-col items-center">
      <Text className="mb-1" size="heading2" weight="medium">
        Welcome to IOPS SPACE
      </Text>
      <Text className="mb-8" size="body2" weight="regular" color="secondary">
        Please sign in or sign up below.
      </Text>

      <ThirdPartyLoginButtonSet {...signinButtonCallbackObj} />
      {isEmailFormShown && (
        <LoginForm
          email={email} 
          setEmail={setEmail} 
          password={password} 
          setPassword={setPassword} 
          onEnterKeyPress={onClickEmailButton}
        />
      )}

      {/* 에러 메시지를 표시하는 부분 */}
      {errorMessage && (
        <div>
          <Text size="body2" weight="regular" color="primary"><div className="text-red-500 mb-3">{errorMessage}</div></Text>
        </div>
      )}

      <div className="mt-2 flex w-full flex-col gap-2">
        <Button
          className="h-10 w-full items-center justify-center"
          text="Login with Email"
          type="blue"
          onClick={onClickEmailButton}
        />
        <Button
          className="h-10 w-full items-center justify-center"
          text="Sign Up"
          type="secondary"
          onClick={onClickSignUpButton}
        />
        <Link to="/reset-email" className="flex h-10 w-full items-center justify-center">
          <Text size="body2" color="primary" weight="regular">
            Forgot Password?
          </Text>
        </Link>
      </div>
    </div>
  );
};

export default SigninForm;
